import type { AccountEvent } from '../../../../../types/account';
import type { AccountEventFormValues } from './AccountEventForm';

type EventPayloadOptions = {
  allowEmptyFormIds?: boolean;
  allowEmptyAssignedStaff: boolean;
  canAssignStaff: boolean;
};

export const sanitizeEmailList = (emails: string[]): string[] => {
  if (!Array.isArray(emails)) return [];
  return Array.from(new Set(emails.map((email) => email.trim()).filter((email) => email.length > 0)));
};

const normaliseGuestsForSubmission = (value: string[]): string[] | undefined => {
  if (!value || !value.length) return undefined;

  const cleaned = sanitizeEmailList(value);

  return cleaned.length ? cleaned : undefined;
};

const buildAssignedStaffIdsPayload = (
  values: AccountEventFormValues,
  allowEmpty: boolean,
  canAssignStaff: boolean,
): number[] | undefined => {
  if (!canAssignStaff || !Array.isArray(values.assigned_staff_ids)) return undefined;

  if (!values.assigned_staff_ids.length && !allowEmpty) return undefined;

  return values.assigned_staff_ids;
};

export const buildAccountEventPayload = (values: AccountEventFormValues, options: EventPayloadOptions) => {
  const locationId = values.location_id && /^\d+$/.test(values.location_id) ? Number(values.location_id) : null;

  return {
    title: values.title,
    event_type: values.event_type || undefined,
    event_start_date: values.event_start_date || undefined,
    event_end_date: values.event_end_date || undefined,
    description: values.description || undefined,
    notes: values.notes || undefined,
    location_id: locationId ?? undefined,
    guests: normaliseGuestsForSubmission(values.guests),
    assigned_staff_ids: buildAssignedStaffIdsPayload(values, options.allowEmptyAssignedStaff, options.canAssignStaff),
  };
};

export const buildAccountEventInitialValues = (event: AccountEvent): Partial<AccountEventFormValues> => ({
  title: event.title ?? '',
  event_type: event.event_type ?? '',
  event_start_date: event.event_start_date ?? undefined,
  event_end_date: event.event_end_date ?? undefined,
  location_id: event.location_id !== null && event.location_id !== undefined ? String(event.location_id) : '',
  description: event.description ?? '',
  notes: event.notes ?? '',
  guests: Array.isArray(event.guests) ? sanitizeEmailList(event.guests.map((guest) => guest.email)) : [],
  assigned_staff_ids: Array.isArray(event.assigned_staff)
    ? event.assigned_staff.map((member) => member.org_staff_id).filter((id): id is number => typeof id === 'number')
    : undefined,
});
